import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";

function Billing() {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pollingStatus, setPollingStatus] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const loadSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription`);
      if (response.data.is_owner === false) {
        toast.error("Billing is managed by your account owner.");
        navigate("/dashboard", { replace: true });
        return null;
      }
      setSubscription(response.data);
      return response.data;
    } catch (error) {
      console.error("Error loading subscription:", error);
      toast.error("Failed to load subscription info");
      return null;
    }
  }, [navigate]);

  const loadTransactions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/billing/history`);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error("Error loading transactions:", error);
    }
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPollingStatus(null);
      toast.error("Payment status check timed out. Please refresh the page.");
      return;
    }

    try {
      const response = await axios.get(`${API}/billing/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid" || response.data.status === "already_processed") {
        setPollingStatus(null);
        toast.success("Payment successful! Your account has been updated.");
        await loadSubscription();
        await loadTransactions();
        navigate('/billing', { replace: true });
        return;
      } else if (response.data.status === "expired") {
        setPollingStatus(null);
        toast.error("Payment session expired. Please try again.");
        navigate('/billing', { replace: true });
        return;
      }

      setPollingStatus("Processing payment...");
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error polling status:", error);
      setPollingStatus(null);
    }
  }, [loadSubscription, loadTransactions, navigate]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const sub = await loadSubscription();
      if (!sub) { setLoading(false); return; }  // member redirected; skip billing fetch
      await loadTransactions();
      setLoading(false);

      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        setPollingStatus("Verifying payment...");
        pollPaymentStatus(sessionId);
      }
    };
    init();
  }, [loadSubscription, loadTransactions, pollPaymentStatus, searchParams]);

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You will be downgraded to the Free plan at the end of your billing period.")) {
      return;
    }

    try {
      await axios.post(`${API}/billing/cancel`);
      toast.success("Subscription will be canceled at the end of the billing period");
      await loadSubscription();
    } catch (error) {
      console.error("Error canceling subscription:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await axios.post(`${API}/billing/customer-portal`, {
        return_url: window.location.href
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error("Could not open billing portal");
      }
    } catch (error) {
      console.error("Error opening customer portal:", error);
      toast.error(error.response?.data?.detail || "Failed to open billing portal. Please ensure you have an active subscription.");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: { bg: '#dcfce7', color: '#16a34a', text: 'Completed' },
      pending: { bg: '#fef3c7', color: '#d97706', text: 'Pending' },
      failed: { bg: '#fee2e2', color: '#dc2626', text: 'Failed' },
      refunded: { bg: '#e0e7ff', color: '#4f46e5', text: 'Refunded' }
    };
    const style = styles[status] || styles.pending;
    return (
      <span style={{
        background: style.bg,
        color: style.color,
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600'
      }}>
        {style.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="loading" data-testid="billing-loading">
        <div className="spinner"></div>
        <p>Loading billing information...</p>
      </div>
    );
  }

  return (
    <div data-testid="billing-page">
      <div className="page-header">
        <h1 className="page-title">Billing</h1>
        <p className="page-description">
          Manage your subscription and view payment history
        </p>
      </div>

      {pollingStatus && (
        <div style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div className="spinner" style={{ width: '24px', height: '24px' }}></div>
          <span style={{ color: '#1e40af', fontWeight: '500' }}>{pollingStatus}</span>
        </div>
      )}

      {/* Payment Issue Alert */}
      {subscription?.payment_status === 'past_due' && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <span style={{ color: '#dc2626', fontSize: '20px' }}>⚠️</span>
          <div>
            <p style={{ color: '#dc2626', fontWeight: '600', marginBottom: '4px' }}>
              Payment Failed
            </p>
            <p style={{ color: '#991b1b', fontSize: '14px' }}>
              We couldn't process your last payment. Please update your payment method to avoid service interruption.
            </p>
            <button
              onClick={handleManageBilling}
              style={{
                marginTop: '12px',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Update Payment Method
            </button>
          </div>
        </div>
      )}

      {/* Current Plan Card */}
      <div className="content-card" style={{ marginBottom: '24px' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="card-title">Current Plan</h2>
          <Button
            onClick={() => navigate('/plans')}
            className="btn btn-primary"
            data-testid="upgrade-plan-btn"
          >
            {subscription?.plan === 'free' ? 'Upgrade Plan' : 'Change Plan'}
          </Button>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '24px',
          padding: '20px',
          background: '#f8fafc',
          borderRadius: '12px'
        }}>
          <div>
            <span style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Plan</span>
            <span style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b' }} data-testid="current-plan-name">
              {subscription?.plan_name || 'Free'}
            </span>
            {subscription?.payment_status === 'past_due' && (
              <span style={{
                display: 'inline-block',
                marginLeft: '8px',
                background: '#fee2e2',
                color: '#dc2626',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                Payment Due
              </span>
            )}
            {subscription?.cancel_at_period_end && (
              <span style={{
                display: 'inline-block',
                marginLeft: '8px',
                background: '#fef3c7',
                color: '#d97706',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                Canceling
              </span>
            )}
            {subscription?.cancel_at_period_end && (
              <p style={{ 
                fontSize: '12px', 
                color: '#d97706', 
                marginTop: '8px',
                background: '#fffbeb',
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #fef3c7'
              }}>
                Your subscription will remain active until {formatDate(subscription?.current_period_end)}. 
                After this date, you'll be moved to the Free plan.
              </p>
            )}
          </div>

          <div>
            <span style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Price</span>
            <span style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>
              {subscription?.price > 0 ? `${formatCurrency(subscription.price)}/mo` : 'Free'}
            </span>
          </div>

          {subscription?.features && !subscription.features.my_leads ? (
            <div>
              <span style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Card Scans This Month</span>
              <span style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b' }} data-testid="billing-card-scans">
                {subscription?.pic_to_lead_usage?.used || 0} / {subscription?.pic_to_lead_usage?.limit ?? 'Unlimited'}
              </span>
            </div>
          ) : (
            <div>
              <span style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Leads Used</span>
              <span style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>
                {subscription?.leads_used || 0} / {subscription?.leads_limit === -1 ? 'Unlimited' : subscription?.leads_limit || 10}
              </span>
              {subscription?.bonus_leads > 0 && (
                <span style={{ fontSize: '12px', color: '#16a34a', display: 'block', marginTop: '2px' }}>
                  +{subscription.bonus_leads} bonus leads
                </span>
              )}
            </div>
          )}

          <div>
            <span style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Billing Period</span>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
              {subscription?.current_period_end ? (
                <>Renews {formatDate(subscription.current_period_end)}</>
              ) : (
                'No active subscription'
              )}
            </span>
          </div>
        </div>

        {/* Features List */}
        <div style={{ marginTop: '20px', padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#16a34a', marginBottom: '12px' }}>Plan Features</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {subscription?.features?.my_leads && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: '#16a34a' }}>&#10003;</span>
                <span style={{ fontSize: '14px', color: '#334155' }}>
                  {subscription?.leads_limit} verified leads/month
                </span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} data-testid="billing-pic-feature">
              <span style={{ color: '#16a34a' }}>&#10003;</span>
              <span style={{ fontSize: '14px', color: '#334155' }}>
                {subscription?.pic_to_lead_usage?.limit == null
                  ? 'Unlimited Pic-to-Lead card scans'
                  : `Pic-to-Lead: ${subscription.pic_to_lead_usage.limit} card scans/month (${subscription.pic_to_lead_usage.remaining} left)`}
              </span>
            </div>
            {subscription?.team_addon && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: '#16a34a' }}>&#10003;</span>
                <span style={{ fontSize: '14px', color: '#334155' }}>Team add-on ({subscription.team_addon_seats} members)</span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#16a34a' }}>&#10003;</span>
              <span style={{ fontSize: '14px', color: '#334155' }}>CRM Integration</span>
            </div>
          </div>
        </div>

        {/* Cancel Subscription Button */}
        {subscription?.plan !== 'free' && !subscription?.cancel_at_period_end && (
          <div style={{ marginTop: '20px' }}>
            <button
              onClick={handleCancelSubscription}
              style={{
                background: 'transparent',
                border: '1px solid #dc2626',
                color: '#dc2626',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
              data-testid="cancel-subscription-btn"
            >
              Cancel Subscription
            </button>
            <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
              Your plan will remain active until the end of the current billing period.
            </p>
          </div>
        )}
      </div>

      {/* Payment Method Card */}
      <div className="content-card" style={{ marginBottom: '24px' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="card-title">Payment Method</h2>
          {subscription?.stripe_customer_id && (
            <Button
              onClick={handleManageBilling}
              className="btn"
              style={{ 
                background: '#f1f5f9', 
                color: '#475569',
                border: '1px solid #e2e8f0'
              }}
              data-testid="manage-billing-btn"
            >
              Manage Billing
            </Button>
          )}
        </div>

        {subscription?.stripe_customer_id ? (
          <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '32px',
                  background: '#1e293b',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  CARD
                </div>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                    Card on file
                  </p>
                  <p style={{ fontSize: '12px', color: '#64748b' }}>
                    Click "Manage Billing" to update your payment method
                  </p>
                </div>
              </div>
              <button
                onClick={handleManageBilling}
                style={{
                  background: 'transparent',
                  border: '1px solid #3b82f6',
                  color: '#3b82f6',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
                data-testid="update-card-btn"
              >
                Update Card
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
            <p>No payment method on file.</p>
            <p style={{ fontSize: '14px', marginTop: '8px' }}>
              Add a payment method by subscribing to a plan.
            </p>
            <Button
              onClick={() => navigate('/plans')}
              className="btn btn-primary"
              style={{ marginTop: '16px' }}
            >
              View Plans
            </Button>
          </div>
        )}
      </div>

      {/* Payment History */}
      <div className="content-card">
        <div className="card-header">
          <h2 className="card-title">Payment History</h2>
        </div>

        {transactions.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Description</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Amount</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, index) => (
                  <tr key={tx.id || index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '14px 16px', fontSize: '14px', color: '#334155' }}>
                      {formatDate(tx.created_at)}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '14px', color: '#334155' }}>
                      {tx.type === 'subscription' ? (
                        <>Subscription - {tx.plan_or_pack?.charAt(0).toUpperCase() + tx.plan_or_pack?.slice(1)} Plan</>
                      ) : tx.type === 'team_addon' ? (
                        <>Team add-on (3 members)</>
                      ) : (
                        <>Lead Pack - {tx.metadata?.leads || ''} leads</>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '14px', color: '#334155', textAlign: 'right', fontWeight: '600' }}>
                      {formatCurrency(tx.amount)}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {getStatusBadge(tx.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>No payment history</p>
            <p style={{ fontSize: '14px' }}>Your transactions will appear here</p>
          </div>
        )}
      </div>

      {/* Need Help Section */}
      <div style={{
        marginTop: '24px',
        padding: '20px',
        background: '#eff6ff',
        borderRadius: '12px',
        border: '1px solid #bfdbfe'
      }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1e40af', marginBottom: '8px' }}>
          Need help with billing?
        </h4>
        <p style={{ fontSize: '14px', color: '#1e40af' }}>
          Contact support for any billing questions or to request a refund.
        </p>
      </div>
    </div>
  );
}

export default Billing;
